import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ChatbotQueryRequest, LlmSection } from "../api";

export type InsightsChatMessage =
  | { id: string; role: "user"; content: string }
  | { id: string; role: "assistant"; content: string; sections?: LlmSection[] };

export type InsightsChatbotConfig = {
  title?: string;
  initialPrompt?: string;
  buildRequest: (question: string) => ChatbotQueryRequest;
};

type InsightsChatbotContextValue = {
  config: InsightsChatbotConfig;
  setConfig: (config: InsightsChatbotConfig) => void;
  draft: string;
  setDraft: (value: string) => void;
  messages: InsightsChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<InsightsChatMessage[]>>;
  resetChat: () => void;
};

const defaultConfig: InsightsChatbotConfig = {
  title: "Peakie AI",
  initialPrompt: "What stands out this week and what should we do next?",
  buildRequest: (question) => ({
    scope: { type: "org" },
    question,
    k: 8,
  }),
};

const STORAGE_KEY = "peak4.insightsChatbot.v1";

const loadPersistedChat = () => {
  if (typeof window === "undefined") return { draft: "", messages: [] as InsightsChatMessage[] };
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { draft: "", messages: [] as InsightsChatMessage[] };
    const parsed = JSON.parse(raw) as any;
    return {
      draft: typeof parsed?.draft === "string" ? parsed.draft : "",
      messages: Array.isArray(parsed?.messages) ? (parsed.messages as InsightsChatMessage[]) : [],
    };
  } catch {
    return { draft: "", messages: [] as InsightsChatMessage[] };
  }
};

const InsightsChatbotContext = createContext<InsightsChatbotContextValue | null>(
  null
);

export const InsightsChatbotProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [config, setConfig] = useState<InsightsChatbotConfig>(defaultConfig);
  const [draft, setDraft] = useState(() => loadPersistedChat().draft);
  const [messages, setMessages] = useState<InsightsChatMessage[]>(
    () => loadPersistedChat().messages
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          draft,
          messages,
        })
      );
    } catch {
      // ignore storage errors (e.g. disabled cookies/storage)
    }
  }, [draft, messages]);

  const resetChat = () => {
    setDraft("");
    setMessages([]);
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  };

  const value = useMemo(
    () => ({
      config,
      setConfig,
      draft,
      setDraft,
      messages,
      setMessages,
      resetChat,
    }),
    [config, draft, messages]
  );

  return (
    <InsightsChatbotContext.Provider value={value}>
      {children}
    </InsightsChatbotContext.Provider>
  );
};

export const useInsightsChatbotConfig = () => {
  const ctx = useContext(InsightsChatbotContext);
  if (!ctx) {
    throw new Error(
      "useInsightsChatbotConfig must be used within InsightsChatbotProvider"
    );
  }
  return ctx;
};
